'use client';

import { c as _c } from "react/compiler-runtime";
import { useDotEnv } from '@repo/dot-env/client';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { PropsWithChildren, useEffect } from 'react';
import { getPostHogInitOptions, type PostHogPublicConfig, shouldInitPostHog } from './posthog-config';

// Module-level guard: each valid configuration initializes at most once per page load,
// regardless of Strict Mode effect replays or provider remounts.
const initializedConfigKeys = new Set<string>();
type PostHogInitClient = Pick<typeof posthog, 'init'>;
export const initPostHog = (config: PostHogPublicConfig, client: PostHogInitClient = posthog): boolean => {
  if (!shouldInitPostHog(config)) return false;
  const configKey = `${config.host}\u0000${config.token}`;
  if (initializedConfigKeys.has(configKey)) return false;
  initializedConfigKeys.add(configKey);
  client.init(config.token, getPostHogInitOptions(config));
  return true;
};

/**
 * PostHog provider configured by the strict public environment context.
 *
 * When the token is missing or the host is not a valid HTTPS URL the
 * provider is a pass-through: no init, no network traffic, no durable
 * identity (local/test/E2E safe default).
 */
export const PosthogProvider = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  const {
    POSTHOG_HOST: host,
    POSTHOG_KEY: token
  } = useDotEnv();
  const enabled = shouldInitPostHog({
    host,
    token
  });
  let t1;
  let t2;
  if ($[0] !== host || $[1] !== token) {
    t1 = () => {
      initPostHog({
        host,
        token
      });
    };
    t2 = [host, token];
    $[0] = host;
    $[1] = token;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (!enabled) {
    return children;
  }
  let t3;
  if ($[4] !== children) {
    t3 = <PostHogProvider client={posthog} data-sentry-element="PostHogProvider" data-sentry-component="PosthogProvider" data-sentry-source-file="posthog-provider.tsx">{children}</PostHogProvider>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};